import concat from 'lodash/concat';
import upperCase from 'lodash/upperCase';

import { TFunction } from '@lyearn/i18n';
import { CheckmarkCircle2Fill } from '@lyearn/icons';
import { ActionMenuItemType } from '@lyearn/molecules';
import { Image } from '@/components/Image';
import { ProviderWithIntegrationDetailFragment } from '@/modules/corporate/modules/integrations/graphql/fragments/generated/ProviderWithIntegrationDetail';
import { IntegrationProviderEnum, RelatedEntityTypeEnum } from '@/types/schema';

import {
  AddTaskMenuAction,
  PROVIDER_AUTO_SYNC_ACTION_MAP,
  PROVIDER_IMPORT_ACTION_MAP,
} from '../types';

const getImportOption = (t: TFunction, provider: ProviderWithIntegrationDetailFragment) => ({
  provider,
  id: PROVIDER_IMPORT_ACTION_MAP[provider.integrationProvider?.provider!],
  value: provider.id,
  label: t('OKRs:ProviderIssues', { providerName: upperCase(provider.name) }),
  EndIcon: () => (
    <Image
      alt={provider.name}
      className="h-20 w-20 flex-shrink-0"
      layout="fill"
      src={provider.logo.url || ''}
    />
  ),
});

const getAutoSyncOption = (t: TFunction, provider: ProviderWithIntegrationDetailFragment) => ({
  provider,
  id: PROVIDER_AUTO_SYNC_ACTION_MAP[provider.integrationProvider?.provider!],
  value: provider.id,
  label: t('OKRs:ViaJQLQuery'),
  EndIcon: () => (
    <Image
      alt={provider.name}
      className="h-20 w-20 flex-shrink-0"
      layout="fill"
      src={provider.logo.url || ''}
    />
  ),
});

const getJiraProviderItems = (t: TFunction, provider: ProviderWithIntegrationDetailFragment) => [
  getImportOption(t, provider),
  getAutoSyncOption(t, provider),
];

const PROVIDER_ACTIONS: Record<
  string,
  (t: TFunction, provider: ProviderWithIntegrationDetailFragment) => ActionMenuItemType[]
> = {
  [IntegrationProviderEnum.Jira]: getJiraProviderItems,
};

const getProviderItems = (
  t: TFunction,
  providers: ProviderWithIntegrationDetailFragment[],
): ActionMenuItemType[] => {
  let actionItems: ActionMenuItemType[] = [];
  providers.forEach((provider) => {
    actionItems = concat(
      actionItems,
      PROVIDER_ACTIONS[provider.integrationProvider?.provider!](t, provider),
    );
  });

  return actionItems;
};

export const getActionMenuItems = (
  t: TFunction,
  providers: ProviderWithIntegrationDetailFragment[] | undefined,
  entity?: RelatedEntityTypeEnum,
): ActionMenuItemType[] => {
  const importFromExistingTaskOption =
    entity &&
    [
      RelatedEntityTypeEnum.MeetingInstance,
      RelatedEntityTypeEnum.AgendaItem,
      RelatedEntityTypeEnum.Okr,
      RelatedEntityTypeEnum.KeyResult,
      RelatedEntityTypeEnum.PeerFeedback,
    ].includes(entity)
      ? [
          {
            id: AddTaskMenuAction.ImportFromExisting,
            label: t('Task:ExistingTasks'),
            EndIcon: CheckmarkCircle2Fill,
          },
        ]
      : [];

  // we don't want integration options for peer feedback
  if (!providers?.length || entity === RelatedEntityTypeEnum.PeerFeedback) {
    return [...importFromExistingTaskOption];
  }

  // removing because agendaItem mei it was breaking @tapaskumar05 @iamsaumya
  const allProviders =
    entity && [RelatedEntityTypeEnum.AgendaItem].includes(entity)
      ? [getProviderItems(t, providers)[0]]
      : getProviderItems(t, providers);

  return [...importFromExistingTaskOption, ...allProviders];
};
